/** Allows view booking confirmations
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { List, Map, Set } from 'immutable';
import moment from 'moment';
import OfferDetails from './OfferDetails';
import SettleInvoiceEditor from './SettleInvoiceEditor';
import { getAuth0UserRoles, settleInvoice } from '../api';
import { useAuth0 } from '@auth0/auth0-react';

const BookingConfirmationResponse = (props) => {
  let { user } = useAuth0();
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    getAuth0UserRoles(user.sub).then((resp) => setRoles(resp.get('roles')));
  }, []);

  // console.log(roles)

  const {
    company,
    updateBookingConfirmation,
    settlingInvoice,
    processingPayment,
    paymentError,
    setSettlingInvoice,
    setProcessingPayment,
    setPaymentError,
    close,
    bookingConfirmations,
    airportByIata = Map(),
  } = props;
  const passengers = getUniquePassengers(bookingConfirmations);
  const currency = bookingConfirmations.get(0).getIn(['orderConfirmation', 'invoice', 'total', 'currency'], 'MUR');

  const absoluteTotal = bookingConfirmations.reduce((total, bc) => bc.getIn(['orderConfirmation', 'invoice', 'total', 'amount'], 0) + total, 0);

  const paymentInstructions = bookingConfirmations.reduce(
    (set, or) =>
      or.get('furtherPaymentRequired') && or.has('orderConfirmation')
        ? set.add(
            Map.of(
              'orderID',
              or.getIn(['orderConfirmation', 'bookingChannelConfirmation']),
              'invoice',
              or.getIn(['orderConfirmation', 'invoice', 'invoiceNumber'])
            )
          )
        : set,
    Set()
  );

  const totalCost = bookingConfirmations.reduce(
    (total, bc) =>
      bc.get('furtherPaymentRequired') || paymentInstructions.size === 0
        ? bc.getIn(['orderConfirmation', 'invoice', 'total', 'amount'], 0) + total
        : total,
    0
  );

  const totalTaxes = bookingConfirmations.reduce(
    (total, bc) =>
      bc.get('furtherPaymentRequired') || paymentInstructions.size === 0
        ? bc
            .getIn(['orderConfirmation', 'invoice', 'total', 'component'], List())
            .reduce((taxes, comp) => (comp.get('description') === 'Taxes' ? comp.get('amount') : 0) + taxes, 0) + total
        : total,
    0
  );

  const totalServiceFees = bookingConfirmations.reduce(
    (total, bc) =>
      bc.get('furtherPaymentRequired') || paymentInstructions.size === 0
        ? bc
            .getIn(['orderConfirmation', 'invoice', 'total', 'component'], List())
            .reduce((serviceFees, comp) => (comp.get('description') === 'Service Fee' ? comp.get('amount') : 0) + serviceFees, 0) + total
        : total,
    0
  );

  const totalBaseFare = totalCost - totalTaxes - totalServiceFees;

  const bookingChannelConfirmationSet = bookingConfirmations.reduce(
    (set, oc) => (oc.has('orderConfirmation') ? set.add(oc.getIn(['orderConfirmation', 'bookingChannelConfirmation'])) : set),
    Set()
  );

  const tripID = bookingChannelConfirmationSet.reduce((idstring, bcc) => idstring + bcc, '');

  const handleCashPayment = () => {
    setSettlingInvoice('cash');
    setProcessingPayment(true);

    let request = new Map()
      .set('payWith', new Map().set('isA', 'BSPCashSettlementInformation').set('authenticationID', user.sub).set('payerName', user.name))
      .set('company', company)
      .set('paymentInstruction', paymentInstructions);

    settleInvoice(request)
      .then((settleInvoiceResponse = Map()) => {
        let acceptedOrders = settleInvoiceResponse
          .get('result')
          .reduce(
            (map, result) =>
              result.has('orderConfirmation')
                ? map.set(result.get('invoice'), result.get('orderConfirmation'))
                : map.set('errors', result.get('description', 'There was some issues with payment.')),
            new Map()
          );
        let newList = bookingConfirmations;

        // console.log(acceptedOrders.toString());

        bookingConfirmations.forEach((v, k) => {
          console.log(v.getIn(['orderConfirmation', 'invoice', 'invoiceNumber']));

          if (acceptedOrders.has(v.getIn(['orderConfirmation', 'invoice', 'invoiceNumber']))) {
            newList = newList.set(
              k,
              Map.of(
                'orderConfirmation',
                acceptedOrders.get(v.getIn(['orderConfirmation', 'invoice', 'invoiceNumber'])),
                'furtherPaymentRequired',
                false
              )
            );
          }
        });
        updateBookingConfirmation(newList);
        if (acceptedOrders.has('errors')) {
          setPaymentError(acceptedOrders.get('errors'));
        }
      })
      .catch((settleInvoiceError) => {
        console.warn(settleInvoiceError);
        setPaymentError('There was an issue with sending the payment. Please try again.');
      });
  };

  return (
    <div className='container itinerary'>
      <GeneralTripInformation
        passengers={passengers}
        tripId={tripID}
        totalCost={absoluteTotal}
        totalCostCurrency={currency}
        hasFlight={true}
        hasHotel={false}
        hasCar={false}
      />

      {passengers.count() > 1 && <TravellingWith passengers={passengers} />}

      {paymentError && (
        <div className='row travelling-with'>
          <div className='col-sm-24' style={{ color: '#d9534f' }}>
            {paymentError !== '' ? paymentError : 'There was some issues with payment.'}
          </div>
        </div>
      )}

      {settlingInvoice === '' ? (
        <div>
          <ServicesDetail
            paymentPending={paymentInstructions.size !== 0}
            setSettlingInvoice={setSettlingInvoice}
            bookingConfirmations={bookingConfirmations}
            airportByIata={airportByIata}
            currency={currency}
            totalCost={totalCost}
            totalTaxes={totalTaxes}
            totalServiceFees={totalServiceFees}
            totalBaseFare={totalBaseFare}
            handleCashPayment={handleCashPayment}
            roles = {roles}
          />

          <div className='row'>
            <div className='col-sm-17 col-md-18 col-lg-19'>
              <div className='add-bookings row greyed-out'>
                <div className='col-md-6 col-lg-5'>
                  <h4 title='Add bookings to trip'>Add bookings</h4>
                </div>
                <div className='col-md-18 col-lg-19'>
                  <nav className='plan-a-trip-nav'>
                    <ul className='row nav'>
                      <li className='col-xs-8'>
                        <a className='book-a-flight active'>Flights</a>
                      </li>
                      <li className='col-xs-8'>
                        <a className='get-a-room'>Accommodation</a>
                      </li>
                      <li className='col-xs-8'>
                        <a className='rent-a-car'>Cars</a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
              <div className='add-services row greyed-out'>
                <div className='col-md-6 col-lg-5'>
                  <h4 title='Add bookings to trip'>Additional services</h4>
                </div>
                <div className='col-md-18 col-lg-19'>
                  <ul className='btn-list clearfix'>
                    <li>
                      <a className='btn btn-primary'>Loyalty Card</a>
                    </li>
                    <li>
                      <a className='btn btn-primary'>Special Meal</a>
                    </li>
                    <li>
                      <a className='btn btn-primary'>Seat</a>
                    </li>
                    <li>
                      <a className='btn btn-primary'>Special Services</a>
                    </li>
                    <li>
                      <a className='btn btn-primary'>Send Passport Info</a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className='col-sm-7 col-md-6 col-lg-5'></div>
          </div>
        </div>
      ) : processingPayment ? (
        <div className='container' style={{ paddingLeft: 0, paddingRight: 10 }}>
          <div className='progress'>
            <div className='progress-bar progress-bar-striped active' role='progressbar' style={{ width: `35%` }}>
              <span className='sr-only'>Processing...</span>
            </div>
          </div>
          {processingPayment && <div style={{ width: '100%', display: 'block', textAlign: 'center' }}>Please wait while we process the payment.</div>}
        </div>
      ) : (
        <SettleInvoiceEditor
          bookingConfirmations={bookingConfirmations}
          setProcessingPayment={setProcessingPayment}
          setPaymentError={setPaymentError}
          company={company}
          updateBookingConfirmation={updateBookingConfirmation}
          paymentInstructions={paymentInstructions}
        />
      )}
      <footer>
        <div className='row footer-buttons'>
          <button type='button' value='Close' onClick={close} className='btn btn-nx btn-danger min-width-140 book'>
            Close
          </button>
        </div>
      </footer>
    </div>
  );
};

const ServicesDetail = ({
  paymentPending,
  setSettlingInvoice,
  bookingConfirmations,
  airportByIata,
  currency,
  totalCost,
  totalTaxes,
  totalServiceFees,
  totalBaseFare,
  handleCashPayment,
  roles
}) => {
  return (
    <div className='row activity-margin'>
      <div className='col-sm-17 col-md-18 col-lg-19 flights-book-list pathway-details'>
        {bookingConfirmations.map((oc, i) => {
          return <OfferDetails key={i} offer={oc.get('orderConfirmation')} airportByIata={airportByIata} defaultCollapseState={false} />;
        })}
      </div>
      <div className='col-sm-7 col-md-6 col-lg-5 trip-summary-col'>
        <div className='col-wrapper'>
          <h2 style={{ backgroundColor: paymentPending ? '#d9534f' : '#09b919' }}>{paymentPending ? 'Payment Pending' : 'Payment Accepted'}</h2>
          <label className='price'>
            <span>
              {currency} <em>{totalCost}</em>
            </span>
          </label>
          <table className='table'>
            <tbody>
              <tr>
                <td>Base</td>
                <td>{totalBaseFare}</td>
              </tr>
              <tr className='tax'>
                <td>Tax</td>
                <td>{totalTaxes}</td>
              </tr>
              <tr>
                <td>Service Fees</td>
                <td>{totalServiceFees}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td>
                  <label className='price'>
                    <span>
                      {currency} <em>{totalCost}</em>
                    </span>
                  </label>
                </td>
              </tr>
            </tfoot>
          </table>
          <div className='clearfix' style={{ display: paymentPending ? 'flex' : 'none' }}>
            <a className='btn btn-primary' style={{ margin: 'auto' }} onClick={() => setSettlingInvoice('card')}>
              Card payment
            </a>
            { roles.indexOf("Agent") > -1 && <a className='btn btn-primary pull-right' onClick={() => handleCashPayment()}>
              BSP Cash
            </a>}
            
          </div>
        </div>
      </div>
    </div>
  );
};

const TravellingWith = ({ passengers }) => {
  return (
    <div className='row travelling-with'>
      <div className='col-xs-24'>
        {' '}
        Travelling with{' '}
        {passengers
          .skip(1)
          .reduce((newList, p) => newList.push(p.getIn(['name', 'first']) + ' ' + p.getIn(['name', 'last'])), List())
          .join(', ')}
      </div>
    </div>
  );
};

const GeneralTripInformation = ({ passengers = Set(), tripId, totalCost, totalCostCurrency, hasFlight, hasHotel, hasCar }) => {
  return (
    <div className='row activity-item'>
      <div className='col-sm-4 hgroup-col'>
        <div className='col-wrapper'>
          <h3>
            <span key='1' title=''>
              {`${passengers.first().getIn(['name', 'first'], 'NN')} ${passengers.first().getIn(['name', 'last'])}`}
            </span>
          </h3>
          <h4>
            <label>TripID</label> <span key='2'>{tripId}</span>
          </h4>
          <i className='tl-corner'></i>
        </div>
      </div>
      <div className='col-sm-6 reason-description-col'>
        <div className='col-wrapper'>
          <h3>Travel Reason &amp; Trip Description</h3>
          <dl>
            <dd>
              <em>not available</em>
            </dd>
          </dl>
        </div>
      </div>
      <div className='col-sm-4 created-by-col'>
        <div className='col-wrapper'>
          <h3>Created By</h3>
          <dl style={{ color: '#333333' }}>
            <dt>
              <em>Anonymous</em>
            </dt>
            <dd>{moment().format('DD-MMM-YYYY')}</dd>
          </dl>
        </div>
      </div>
      <div className='col-sm-6 destination-col'>
        {/*      <div className="col-wrapper">
        <h3>LHR</h3>
        <dl>
          <dt title="London (LHR), United Kingdom">London</dt>
          <dd>15-June-2017</dd>
        </dl>
      </div>*/}
      </div>
      <div className='col-sm-4 price-col'>
        <div className='col-wrapper'>
          <label className='icons-included'>
            {hasFlight && <span className='flight'></span>}
            {hasHotel && <span className='hotel'></span>}
            {hasCar && <span className='car'></span>}
            {/*<span className="misc"></span>*/}
          </label>
          <label className='price'>
            <span>
              {totalCostCurrency} <em>{totalCost}</em>
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

const getUniquePassengers = (bookingConfirmations = List()) => {
  return bookingConfirmations.reduce(
    (m, bc) =>
      bc
        .get('orderConfirmation', Map())
        .get('participant', List())
        .reduce((map, passenger) => {
          const key =
            passenger.getIn(['name', 'first']).toString() + passenger.getIn(['name', 'last']).toString() + passenger.get('birthDate').toString();
          return map.set(key.replace(' ', ''), passenger);
        }, m),
    Map()
  );
};

BookingConfirmationResponse.propTypes = {
  // Close booking confirmation view. () -> ()
  close: PropTypes.func.isRequired,
  // List<BookingConfirmationAsMap>
  bookingConfirmations: ImmutablePropTypes.listOf(ImmutablePropTypes.map).isRequired,
};

export default BookingConfirmationResponse;



// WEBPACK FOOTER //
// ./src/ndc/booking/BookingConfirmationResponse.js